import { useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface DeleteProfessionalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  professional: {
    id: string;
    full_name: string | null;
    total_patients: number;
    total_documents: number;
    total_results: number;
  };
  onSuccess: () => void;
}

export function DeleteProfessionalModal({
  open,
  onOpenChange,
  professional,
  onSuccess
}: DeleteProfessionalModalProps) {
  const [confirmation, setConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState<{
    patients: number;
    documents: number;
    lab_results: number;
    health_records: number;
    categories: number;
    subcategories: number;
    files: number;
    total_deleted: number;
  } | null>(null);

  const confirmationText = "EXCLUIR PERMANENTEMENTE";
  const isConfirmed = confirmation === confirmationText;

  const handleDelete = async () => {
    if (!isConfirmed) return;

    try {
      setIsDeleting(true);

      const { data, error } = await supabase.functions.invoke('delete-professional-cascade', {
        body: { professionalId: professional.id }
      });

      if (error) throw error;

      setStats(data.stats);

      toast.success('Profissional excluído com sucesso', {
        description: `${data.stats.total_deleted} registros removidos permanentemente`
      });

      setTimeout(() => {
        onSuccess();
        onOpenChange(false);
        setConfirmation("");
        setStats(null);
      }, 3000);

    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir profissional', {
        description: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <AlertDialogTitle>Excluir Profissional Permanentemente</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-4 pt-4">
            {stats ? (
              <Alert>
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">✅ Exclusão concluída com sucesso!</p>
                    <div className="text-sm space-y-1">
                      <p>• {stats.patients} pacientes</p>
                      <p>• {stats.documents} documentos</p>
                      <p>• {stats.lab_results} resultados de exames</p>
                      <p>• {stats.health_records} registros de saúde</p>
                      <p>• {stats.categories} categorias</p>
                      <p>• {stats.subcategories} subcategorias</p>
                      <p>• {stats.files} arquivos</p>
                      <p className="font-semibold mt-2">Total: {stats.total_deleted} registros removidos</p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-semibold">⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!</p>
                    <p className="mt-2">Você está prestes a excluir permanentemente:</p>
                  </AlertDescription>
                </Alert>

                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <p className="font-semibold">Profissional: {professional.full_name}</p>
                  <div className="text-sm space-y-1 text-muted-foreground">
                    <p>• {professional.total_patients} pacientes</p>
                    <p>• {professional.total_documents} documentos</p>
                    <p>• {professional.total_results} resultados de exames</p>
                    <p>• Todos os registros de saúde, categorias e subcategorias</p>
                    <p>• Todos os arquivos no storage</p>
                    <p>• Conta de autenticação (auth.users)</p>
                  </div>
                </div>

                <Alert>
                  <AlertDescription>
                    <p className="font-semibold mb-2">Para confirmar, digite:</p>
                    <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                      {confirmationText}
                    </code>
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="confirmation">Confirmação</Label>
                  <Input
                    id="confirmation"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder="Digite a confirmação aqui"
                    disabled={isDeleting}
                    className="font-mono"
                  />
                </div>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            {stats ? 'Fechar' : 'Cancelar'}
          </Button>
          {!stats && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!isConfirmed || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir Permanentemente
                </>
              )}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
